import {useEffect, useState} from 'react'
import {checkElement} from '../../../utils/drawing'
import {useWindowSize} from '../../../hooks/window-size'
import {select} from 'd3-selection'
import isEmpty from 'lodash/isEmpty'

let selectedAssets = []

export const useAssetHighlight = ({
                                    svgElement,
                                    isLoaded: isDrawingLoaded,
                                    highlightAssets,
                                    highlights,
                                    onHighlightClick
                                  }) => {


  const windowSize = useWindowSize()
  const [assets, setAssets] = useState({})


  useEffect(
    () => {
      if (isDrawingLoaded && highlightAssets && svgElement) {
        let svg = select(svgElement)
        svg.selectAll('#asset-labels').style('pointer-events', 'none')
        svg.selectAll('#assets').style('pointer-events', 'none')

        highlightAssets.forEach(highlightAsset => {
          let assetGroup = highlightAsset.assetType + '-assets'
          let targetAssets = svg.selectAll('#' + assetGroup)
          let assetsByType = {}
          const {
            stroke,
            strokeWidth,
            fill,
            multipleSelection,
            onClick
          } = highlightAsset

          targetAssets.style('pointer-events', 'auto')
          targetAssets.selectAll('*').each(function() {
            let assetEl = select(this)
            assetEl.style('pointer-events', 'auto')
            let assetNode = assetEl.node()
            let assetId = assetNode.id
            assetsByType[assetId] = {
              initialHighlight: {
                stroke,
                strokeWidth,
                fill
              },
              highlights: []
            }
            if (onClick) {
              assetEl.on('click', function() {
                const {handler} = onClick
                const isSelected = !(selectedAssets.indexOf(assetId) === -1)
                handler(assetId, isSelected)
              })
            }
          })
          if (assets[highlightAsset.assetType]) {
            assets[highlightAsset.assetType] = {
              assetsByType: assetsByType,
              multipleSelection
            }
          } else {
            assets[highlightAsset.assetType] = {}
            assets[highlightAsset.assetType] = {
              assetsByType: assetsByType,
              multipleSelection
            }
          }
        })
        setAssets(assets)
      }
      return () => {
        setAssets({})
        selectedAssets = []
      }
    },
    /* not including asset dependency to eliminate recursive renders */
    [highlightAssets, isDrawingLoaded, svgElement]
  )


  useEffect(
    () => {
      if (isDrawingLoaded && !isEmpty(assets)) {
        const {assetType, selectable, reset} = highlights
        if (assetType) {
          let {assetsByType, multipleSelection = false} = assets[assetType]
          if (!isEmpty(assetsByType)) {
            resetHighlights({assetsByType, reset})
            addHighlights({assetsByType, highlights, multipleSelection})
            showHighlights({
              svgElement,
              assetsByType,
              selectable,
              onSelectorClick: onHighlightClick ? onSelectorClick : null
            })
          }
        }
      }
    },
    [assets, highlights, isDrawingLoaded, svgElement]
  )

  const onSelectorClick = elementId => {
    const rect = getElementRect({elementId})

    if (rect) {
      const {x, y} = getPopupPosition({
        height: windowSize.innerHeight,
        width: windowSize.innerWidth,
        rect
      })
      const [blId, flId, rmId] = elementId.split(';')

      onHighlightClick({
        room: {blId, flId, rmId},
        position: {x, y}
      })
    }
  }

  return {assets}
}

const resetHighlights = ({assetsByType, reset}) => {
  if (reset) {
    Object.keys(assetsByType).forEach(assetId => {
      assetsByType[assetId].highlights = []
      if (assetsByType[assetId].showSelector) {
        assetsByType[assetId].showSelector = false
        removeSelector(assetId)
      }
    })
  }
}

const addHighlights = ({assetsByType, highlights, multipleSelection}) => {
  const {highlights: assetIds, show, selectable, selector} = highlights
  if (!isEmpty(assetIds)) {
    Object.keys(assetIds).forEach(assetId => {
      if (assetsByType[assetId]) {
        // asset exists on drawing
        const highlight = assetIds[assetId]
        if (show) {
          if (assetsByType[assetId] && assetsByType[assetId].highlights) {
            // if assetId already selected, highlight on top
            if (assetId === getSelectedAssetId(assetId)) {
              assetsByType[assetId].highlights.unshift(highlight)
            } else {
              assetsByType[assetId].highlights.push(highlight)
            }
          } else {
            assetsByType[assetId].highlights = []
            assetsByType[assetId].highlights.push(highlight)
          }
        } else {
          if (
            assetsByType[assetId] &&
            assetsByType[assetId].highlights &&
            !selectable
          ) {
            if (assetId === getSelectedAssetId(assetId)) {
              assetsByType[assetId].highlights = [
                assetsByType[assetId].initialHighlight
              ]
            } else {
              assetsByType[assetId].highlights.pop()
            }
          }
        }
        if (selectable) {
          mapSelectedAssets({
            assetsByType,
            assetId,
            show,
            multipleSelection,
            selector
          })
        } else if (selector) {
          assetsByType[assetId].showSelector = show
        }
      } else {
       // console.log('Asset %s not found on the drawing.', assetId)
      }
    })
  }
}

const showHighlights = ({
                          svgElement,
                          assetsByType,
                          selectable,
                          onSelectorClick
                        }) => {
  Object.keys(assetsByType).forEach(assetId => {
    const asset = assetsByType[assetId]
    const {highlights, showSelector} = asset
    if (highlights) {
      let element = document.getElementById(assetId)
      if (element) {
        if (highlights.length > 0) {
          highlights.forEach(highlight => {
            element.style.strokeWidth = highlight.strokeWidth
            element.style.stroke = highlight.stroke
            element.style.fill = highlight.fill
            element.style.fillOpacity = highlight.fillOpacity || '0.9'
          })
        } else {
          element.style.strokeWidth = ''
          element.style.stroke = ''
          element.style.fill = ''
          element.style.fillOpacity = ''
          element.removeEventListener('click', highlightClicked, true)
        }
      }
    }
    if (typeof showSelector !== 'undefined' && showSelector != null) {
      if (showSelector) {
        const selectorEl = document.querySelector(
          '[id=\'selector-' + assetId + '\']'
        )
        if (!selectorEl) {
          addSelector(svgElement, assetId, selectable, onSelectorClick)
        }
      } else {
        removeSelector(assetId)
      }
    }
  })
}

const mapSelectedAssets = ({
                             assetsByType,
                             assetId,
                             show,
                             multipleSelection,
                             selector
                           }) => {
  let selectedIndex = selectedAssets.findIndex(id => {
    return id === assetId
  })
  if (selectedIndex >= 0) {
    // asset exists remove it from list
    selectedAssets.splice(selectedIndex, 1)
    assetsByType[assetId].highlights.pop()
  } else {
    if (!multipleSelection) {
      const previousSelectedAssetId = selectedAssets[0]
      if (previousSelectedAssetId) {
        assetsByType[previousSelectedAssetId].highlights.pop() // reset previous asset highlight
        if (selector) {
          assetsByType[previousSelectedAssetId].showSelector = false
        }
      }
      selectedAssets = []
    }
    selectedAssets.push(assetId)
  }
  if (selector) {
    assetsByType[assetId].showSelector = show
  }
}

const getSelectedAssetId = assetId =>
  selectedAssets.filter(selectedAssetId => selectedAssetId === assetId)[0]

const shiftY = (r) => (val) => r * val;

const getSelectorPosition = ({bbox, units}) => {
  let x = bbox.x + bbox.width / 2
  let y = bbox.y + bbox.height / 2
  let r;

  switch (units) {
    case 'mm':
      r = 1000
      y = y - shiftY(r)(1.8)
      break
    case 'cm':
      r = 100
      y = y - shiftY(r)(1.8)
      break
    case 'Metres':
    case 'm':
      r = 1
      y = y - shiftY(r)(1.8)
      break
    default:
      r = 30
      y = y - shiftY(r)(1.8) - r / 2		// slightly above center to not cover label
      break
  }

  return ({x,y,r});
}
const addSelector = (svgElement, elementId, selectable, onSelectorClicked) => {
  let element = document.getElementById(elementId)
  if (onSelectorClicked && !selectable) {
    element.addEventListener(
      'click',
      highlightClicked.bind(this, onSelectorClicked),
      true
    )
  }
  let svg = select(svgElement)
  let g = svg.select('#viewer').select('#mirror')

  let bbox = element.getBBox()
  let defaultView = svg.select('#defaultView')
  let units = defaultView.attr('units')

  const {x,y,r} = getSelectorPosition({bbox, units});

  const p1 = shiftY(r)(1.2);
  const p2 = shiftY(r)(1.6);

  let container = g.append('g').attr('id', 'outer_g_' + elementId)

  container
    .attr('id', 'selector-' + elementId)

  container.append('polygon')
    .attr('points', `${-p1}, 0 0,${p2} ${p1},0`)
    .attr('fill', 'white')

  container
    .append('circle')
    .attr('r', r * 1.2)
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('fill', 'white')

  container
    .append('circle')
    .attr('r', r)
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('fill', '#3395FE')

  if (onSelectorClicked) {
    container.on('click', () => onSelectorClicked(elementId))
  }

  container.attr('transform', `translate(${x},${y})`)
}

const removeSelector = elementId => {
  checkElement(elementId).then(() => {
    const selectorEl = document.querySelector(
      '[id=\'selector-' + elementId + '\']'
    )
    if (selectorEl) {
      selectorEl.remove()
    }
  })
}

const highlightClicked = (onSelectorClicked, evt) => {
  evt.stopPropagation()
  onSelectorClicked(evt.target.id)
}

const getElementRect = ({elementId}) => {
  let roomElement = document.getElementById(elementId)
  let rect = null

  if (roomElement) {
    rect = roomElement.getBoundingClientRect()
  }

  return rect
}

const getPopupPosition = ({height, width, rect}) => {
  let y = 100
  let x = 200

  if (rect) {
    y = parseInt(rect.y) + rect.height / 2
    if (y > height - 162) {
      y = height - 162
    }

    x = parseInt(rect.x)
    let elementMidX = x + rect.width / 2
    x = elementMidX - 150
    if (x > width - 302) {
      x = width - 302
    }
    if (x < 10) {
      x = 4
    }
  }

  return {x, y}
}
